import { useId, useRef, useState } from 'react';
import { ArrowUturnLeftIcon, LockClosedIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import Popup from '../../../../../../../components/general/Popup.tsx';
import { PrimaryButton } from '../../../../../../../components/general/PrimaryButton.tsx';
import { SecondaryButton } from '../../../../../../../components/general/SecondaryButton.tsx';
import { VatDeclarationForm } from '../../../../../../../components/specific/VatDeclaration/index.ts';
import { cn, formatDateTime } from '../../../../../../../lib/utils.ts';
import {
    VatReturnApprovalDetails,
    VatReturnApprovalStatusLabel,
} from './VatReturnApprovalStatus.tsx';
import type { VatDeclarationSnapshotSummary } from '../../models.ts';
import type { SavedVatDeclarationSnapshot } from '../useVatReturns.ts';
import type { VatReturnPeriod } from '../types/index.ts';
import { vatReturnPeriodLabel, vatReturnPeriodRangeText } from '../utils/index.ts';
import { VatReturnCompletenessCard } from './VatReturnCompletenessCard.tsx';
import { VatReturnExcludedDocuments } from './VatReturnExcludedDocuments.tsx';

export interface VatReturnVersionViewProps {
    businessId: string;
    clientName: string;
    period: VatReturnPeriod;
    version: VatDeclarationSnapshotSummary;
    snapshot: SavedVatDeclarationSnapshot | undefined;
    isLoading: boolean;
    hasLoadError: boolean;
    isRequestingApproval: boolean;
    isWithdrawingApproval: boolean;
    onSelectVersion: (snapshotId: string) => void;
    onRequestApproval: () => void;
    onWithdrawApproval: (requestId: string) => void;
}

function savedBy(version: VatDeclarationSnapshotSummary): string {
    return version.created_by_name ? ` door ${version.created_by_name}` : '';
}

function sendBlockReason(
    version: VatDeclarationSnapshotSummary,
    isLatest: boolean,
    isLoading: boolean,
    hasLoadError: boolean,
    hasSnapshot: boolean,
): string | null {
    if (!isLatest) {
        return 'Alleen de nieuwste versie van een periode kan worden verstuurd';
    }
    if (version.approval_status === 'pending') {
        return 'Deze versie wacht al op goedkeuring van de klant';
    }
    if (version.approval_status === 'approved') {
        return 'Deze versie is al goedgekeurd door de klant';
    }
    if (isLoading) {
        return 'Deze versie wordt nog geladen';
    }
    if (hasLoadError) {
        return 'Deze versie kon niet worden geladen';
    }
    if (!hasSnapshot) {
        return 'Deze versie wordt nog geladen';
    }
    return null;
}

export function VatReturnVersionView({
    businessId,
    clientName,
    period,
    version,
    snapshot,
    isLoading,
    hasLoadError,
    isRequestingApproval,
    isWithdrawingApproval,
    onSelectVersion,
    onRequestApproval,
    onWithdrawApproval,
}: VatReturnVersionViewProps) {
    const headingId = useId();
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const hasConfirmedSendRef = useRef(false);
    const [withdrawConfirmationRequestId, setWithdrawConfirmationRequestId] = useState<string | null>(null);
    const hasConfirmedWithdrawRef = useRef(false);
    const label = vatReturnPeriodLabel(period.periodStart, period.periodEnd);
    const rangeText = vatReturnPeriodRangeText(period.periodStart, period.periodEnd);
    const latest = period.latestVersion;
    const isLatest = latest?.id === version.id;
    const blockReason = sendBlockReason(version, isLatest, isLoading, hasLoadError, snapshot !== undefined);
    const isSendBlocked = blockReason !== null || isRequestingApproval;
    const withdrawableRequestId = isLatest && version.approval_status === 'pending' ? version.approval_request_id : null;
    const sendTitle = isLatest && version.approval_status === 'withdrawn' ? 'Opnieuw versturen' : 'Ter goedkeuring versturen';

    function openSendConfirmation() {
        hasConfirmedSendRef.current = false;
        setIsConfirmationOpen(true);
    }

    function confirmSend() {
        setIsConfirmationOpen(false);
        if (isSendBlocked || hasConfirmedSendRef.current) {
            return;
        }
        hasConfirmedSendRef.current = true;
        onRequestApproval();
    }

    function openWithdrawConfirmation() {
        hasConfirmedWithdrawRef.current = false;
        setWithdrawConfirmationRequestId(withdrawableRequestId);
    }

    function confirmWithdraw() {
        const requestId = withdrawConfirmationRequestId;
        setWithdrawConfirmationRequestId(null);
        if (requestId === null || requestId !== withdrawableRequestId || isWithdrawingApproval || hasConfirmedWithdrawRef.current) {
            return;
        }
        hasConfirmedWithdrawRef.current = true;
        onWithdrawApproval(requestId);
    }

    return (
        <section aria-labelledby={headingId} className="flex min-w-0 flex-col gap-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 id={headingId} className="text-2xl font-semibold text-gray-900">BTW-aangifte {label}</h2>
                        <VatReturnApprovalStatusLabel approval={version} clientName={clientName} size="md" />
                    </div>
                    {label !== rangeText && <p className="text-sm text-gray-500">{rangeText}</p>}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {withdrawableRequestId !== null && (
                        <SecondaryButton
                            title={isWithdrawingApproval ? 'Intrekken...' : 'Verzoek intrekken'}
                            icon={ArrowUturnLeftIcon}
                            disabled={isWithdrawingApproval}
                            onClick={openWithdrawConfirmation}
                        />
                    )}
                    <PrimaryButton
                        title={isRequestingApproval ? 'Versturen...' : sendTitle}
                        icon={PaperAirplaneIcon}
                        disabled={isSendBlocked}
                        disabledFeedback={blockReason ?? undefined}
                        onClick={openSendConfirmation}
                    />
                </div>
            </div>
            {blockReason !== null && !isLoading && !hasLoadError && (
                <p className="text-right text-sm text-gray-500">{blockReason}.</p>
            )}

            {!isLatest && latest && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <span className="flex items-center gap-2">
                        <LockClosedIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        Je bekijkt een oudere versie, opgeslagen op {formatDateTime(version.created_at)}{savedBy(version)}. Deze versie staat vast.
                    </span>
                    <button
                        type="button"
                        onClick={() => onSelectVersion(latest.id)}
                        className="font-semibold text-amber-900 underline underline-offset-2"
                    >
                        Ga naar versie {latest.version}
                    </button>
                </div>
            )}
            {isLatest && version.approval_status === null && (
                <p className="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                    <LockClosedIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Opgeslagen op {formatDateTime(version.created_at)}{savedBy(version)}. Deze versie staat vast en is nog niet verstuurd ter goedkeuring.
                </p>
            )}
            <VatReturnApprovalDetails approval={version} clientName={clientName} />

            {isLoading && <p role="status" className="text-sm text-gray-500">Versie laden...</p>}
            {!isLoading && hasLoadError && (
                <p role="alert" className="text-sm text-red-700">Deze versie kon niet worden geladen. Probeer het opnieuw.</p>
            )}
            {snapshot && !isLoading && !hasLoadError && (
                <div className="flex flex-col gap-5">
                    <div className={cn('grid grid-cols-1 gap-4', snapshot.unused_items.length > 0 && 'md:grid-cols-2')}>
                        <VatReturnCompletenessCard
                            documentCount={snapshot.completeness.document_count}
                            matchedCount={snapshot.completeness.matched_count}
                            matchedPercentage={snapshot.completeness.matched_percentage}
                            periodText={`tussen ${vatReturnPeriodRangeText(period.periodStart, period.periodEnd, ' en ')}`}
                        />
                        <VatReturnExcludedDocuments businessId={businessId} unusedItems={snapshot.unused_items} />
                    </div>
                    <VatDeclarationForm businessId={businessId} vatDeclaration={snapshot} />
                </div>
            )}

            <Popup
                open={isConfirmationOpen}
                onClose={() => setIsConfirmationOpen(false)}
                onConfirm={confirmSend}
                title={`${sendTitle}?`}
                confirmButtonDisplayLabel="Versturen"
                confirmButtonDisabled={isSendBlocked}
            >
                <p className="text-sm text-gray-600">
                    De klant kan versie {version.version} van {label} goedkeuren of afwijzen. Zolang de klant niet heeft
                    gereageerd, kun je het verzoek intrekken. Na goedkeuring kun je voor deze periode geen nieuwe versie
                    meer opslaan.
                </p>
            </Popup>
            <Popup
                open={withdrawConfirmationRequestId !== null && withdrawConfirmationRequestId === withdrawableRequestId}
                onClose={() => setWithdrawConfirmationRequestId(null)}
                onConfirm={confirmWithdraw}
                title="Verzoek intrekken?"
                confirmButtonDisplayLabel="Intrekken"
                confirmButtonDisabled={isWithdrawingApproval}
                variant="danger"
            >
                <p className="text-sm text-gray-600">
                    De klant kan deze versie dan niet meer goedkeuren of afwijzen. Je kunt de versie daarna opnieuw
                    versturen of een nieuwe versie opslaan.
                </p>
            </Popup>
        </section>
    );
}
